/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  theme,
} from "@nordcloud/gnui";
import { ReportStatus as ReportStatusType } from "~/generated/graphql";
import { TooltipColor } from "~/types/TooltipColor";

type Props = {
  status: ReportStatusType;
};

export function ReportStatus({ status }: Props) {
  const statusInfo = getReportStatus(status);

  return (
    <FlexContainer justifyContent="center" alignItems="center">
      <ExtendedTooltip
        status={statusInfo.tooltipColor}
        caption={statusInfo.description}
        hideTimeout={0}
        position="start"
      >
        <FlexContainer justifyContent="center" alignItems="center">
          <SVGIcon
            size="md"
            name={statusInfo.icon}
            color={statusInfo.color()}
          />
        </FlexContainer>
      </ExtendedTooltip>
    </FlexContainer>
  );
}

function getReportStatus(status: ReportStatusType) {
  switch (status) {
    case ReportStatusType.Completed:
      return {
        description: "Completed",
        icon: "successFilled" as const,
        color: () => `${theme.color.background.success}`,
        tooltipColor: TooltipColor.success,
      };
    case ReportStatusType.Failed:
      return {
        description: "Failed",
        icon: "cancelFilled" as const,
        color: () => `${theme.color.background.error}`,
        tooltipColor: TooltipColor.danger,
      };
    case ReportStatusType.InProgress:
      return {
        description: "In Progress",
        icon: "inProgressFilled" as const,
        color: () => `${theme.color.background.info}`,
        tooltipColor: TooltipColor.notification,
      };
    default:
      return {
        description: "N/A",
        icon: "playFilled" as const,
        color: () => `${theme.color.support.grey}`,
      };
  }
}
